import React, { useState, useEffect } from 'react';
import Layout from '../../components/ui/Layout';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../hooks/useChat';
import { useRetirementPlansRealtime, useRetirementScenariosRealtime } from '../../hooks/useRealtimeSubscription';
import { useDoubleTap, useLongPress } from '../../hooks/useGestures';
import SwipeableCard from '../../components/ui/SwipeableCard';
import { Checkbox } from '../../components/ui/Checkbox';
import { updateRetirementScenario, deleteRetirementScenario } from '../../services/supabaseService';
import toast from 'react-hot-toast';
import RetirementForm from './components/RetirementForm';
import ScenarioCard from './components/ScenarioCard';
import ReadinessScore from './components/ReadinessScore';
import RetirementTimeline from './components/RetirementTimeline';
import { triggerRetirementConfetti } from '../../utils/confetti';
import CelebrationModal from '../../components/ui/CelebrationModal';
import ErrorMessage from '../../components/ui/ErrorMessage';
import ConfirmationModal from '../../components/ui/ConfirmationModal';


const RetirementCalculator = () => {
  const { user } = useAuth();
  const [retirementPlan, setRetirementPlan] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedScenarios, setSelectedScenarios] = useState(new Set());
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);
  const [previousReadinessScore, setPreviousReadinessScore] = useState(0);
  const [showDeleteScenarioConfirm, setShowDeleteScenarioConfirm] = useState(false);
  const [scenarioToDelete, setScenarioToDelete] = useState(null);
  const [deleteScenarioLoading, setDeleteScenarioLoading] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const { response, isLoading: aiLoading, error: aiError, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  const [errors, setErrors] = useState([]);
  const retryError = (errorId) => {
    setErrors((prev) => prev?.filter((e) => e?.id !== errorId));
  };
  const removeError = (errorId) => {
    setErrors((prev) => prev?.filter((e) => e?.id !== errorId));
  };

  useEffect(() => {
    if (user) {
      fetchRetirementPlan();
    }
  }, [user]);

  useEffect(() => {
    if (aiError) {
      toast?.error(aiError?.message);
    }
  }, [aiError]);

  useEffect(() => {
    if (retirementPlan) {
      const currentScore = retirementPlan?.readiness_score || 0;
      
      // Trigger celebration when crossing readiness score thresholds (50%, 75%, 90%, 100%)
      const thresholds = [50, 75, 90, 100];
      const crossedThreshold = thresholds?.find(
        threshold => previousReadinessScore < threshold && currentScore >= threshold
      );
      
      if (crossedThreshold) {
        triggerRetirementConfetti();
        setCelebrationData({
          score: currentScore,
          threshold: crossedThreshold
        });
        setShowCelebration(true);
      }
      
      setPreviousReadinessScore(currentScore);
    }
  }, [retirementPlan, previousReadinessScore]);

  const fetchRetirementPlan = async () => {
    try {
      setLoading(true);
      const { data: planData, error: planError } = await supabase?.from('retirement_plans')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(1)?.maybeSingle();

      if (planError) throw planError;

      if (planData) {
        setRetirementPlan(planData);
        fetchScenarios(planData?.id);
      } else {
        setShowForm(true);
      }
    } catch (error) {
      console.error('Error fetching retirement plan:', error);
      toast?.error('Failed to load retirement plan');
    } finally {
      setLoading(false);
    }
  };

  const fetchScenarios = async (planId) => {
    try {
      const { data, error } = await supabase?.from('retirement_scenarios')?.select('*')?.eq('retirement_plan_id', planId)?.order('scenario_type', { ascending: true });

      if (error) throw error;
      setScenarios(data || []);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
    }
  };

  // Real-time subscriptions
  useRetirementPlansRealtime(user?.id, {
    onUpdate: (updatedPlan) => {
      if (updatedPlan?.id === retirementPlan?.id) {
        setRetirementPlan(updatedPlan);
      }
    },
    onDelete: (deletedPlan) => {
      if (deletedPlan?.id === retirementPlan?.id) {
        setRetirementPlan(null);
        setScenarios([]);
        setShowForm(true);
        toast?.success('Retirement plan deleted');
      }
    }
  });

  useRetirementScenariosRealtime(user?.id, {
    onInsert: (newScenario) => {
      if (newScenario?.retirement_plan_id === retirementPlan?.id && !newScenario?.is_archived) {
        setScenarios(prev => [...prev, newScenario]?.sort((a, b) => 
          a?.scenario_type?.localeCompare(b?.scenario_type)
        ));
      }
    },
    onUpdate: (updatedScenario) => {
      setScenarios(prev => {
        if (updatedScenario?.is_archived) {
          return prev?.filter(s => s?.id !== updatedScenario?.id);
        }
        return prev?.map(s => s?.id === updatedScenario?.id ? updatedScenario : s);
      });
    },
    onDelete: (deletedScenario) => {
      setScenarios(prev => prev?.filter(s => s?.id !== deletedScenario?.id));
    }
  });

  const handleToggleFavoriteScenario = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    // Optimistic update
    setScenarios(prev => prev?.map(s => 
      s?.id === id ? { ...s, is_favorite: newStatus } : s
    ));

    try {
      await updateRetirementScenario(id, { isFavorite: newStatus });
      if ('vibrate' in navigator) navigator.vibrate(15);
    } catch (error) {
      // Rollback on error
      setScenarios(prev => prev?.map(s => 
        s?.id === id ? { ...s, is_favorite: currentStatus } : s
      ));
      console.error('Error toggling favorite:', error);
      toast?.error('Failed to update favorite');
    }
  };

  const handleArchiveScenario = async (id) => {
    // Optimistic update
    setScenarios(prev => prev?.filter(s => s?.id !== id));

    try {
      await updateRetirementScenario(id, { isArchived: true });
      toast?.success('Scenario archived');
    } catch (error) {
      // Rollback on error
      fetchScenarios(retirementPlan?.id);
      console.error('Error archiving scenario:', error);
      toast?.error('Failed to archive scenario');
    }
  };

  const handleDeleteScenario = async (id) => {
    const scenario = scenarios?.find(s => s?.id === id);
    setScenarioToDelete(scenario);
    setShowDeleteScenarioConfirm(true);
  };

  const confirmDeleteScenario = async () => {
    if (!scenarioToDelete) return;
    setDeleteScenarioLoading(true);
    // Optimistic update
    setScenarios(prev => prev?.filter(s => s?.id !== scenarioToDelete?.id));
    try {
      await deleteRetirementScenario(scenarioToDelete?.id);
      toast?.success('Scenario deleted');
      setShowDeleteScenarioConfirm(false);
      setScenarioToDelete(null);
    } catch (error) {
      // Rollback on error
      fetchScenarios(retirementPlan?.id);
      console.error('Error deleting scenario:', error);
      toast?.error('Failed to delete scenario');
    } finally {
      setDeleteScenarioLoading(false);
    }
  };

  const handleLongPress = () => {
    setSelectionMode(true);
    if ('vibrate' in navigator) navigator.vibrate(20);
  };

  const handleToggleSelection = (id) => {
    setSelectedScenarios(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(id)) {
        newSet?.delete(id);
      } else {
        newSet?.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedScenarios(new Set(scenarios?.map(s => s?.id)));
  };

  const handleDeselectAll = () => {
    setSelectedScenarios(new Set());
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedScenarios(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedScenarios?.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    const idsToDelete = Array.from(selectedScenarios);
    setBulkDeleteLoading(true);
    // Optimistic update
    setScenarios(prev => prev?.filter(s => !idsToDelete?.includes(s?.id)));
    try {
      await Promise.all(idsToDelete?.map(id => deleteRetirementScenario(id)));
      toast?.success(`Deleted ${idsToDelete?.length} scenarios`);
      handleCancelSelection();
      setShowBulkDeleteConfirm(false);
    } catch (error) {
      // Rollback on error
      fetchScenarios(retirementPlan?.id);
      console.error('Error bulk deleting:', error);
      toast?.error('Failed to delete scenarios');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedScenarios?.size === 0) return;

    const idsToArchive = Array.from(selectedScenarios);
    // Optimistic update
    setScenarios(prev => prev?.filter(s => !idsToArchive?.includes(s?.id)));

    try {
      await Promise.all(idsToArchive?.map(id => updateRetirementScenario(id, { isArchived: true })));
      toast?.success(`Archived ${idsToArchive?.length} scenarios`);
      handleCancelSelection();
    } catch (error) {
      // Rollback on error
      fetchScenarios(retirementPlan?.id);
      console.error('Error bulk archiving:', error);
      toast?.error('Failed to archive scenarios');
    }
  };

  const EnhancedScenarioCard = ({ scenario }) => {
    const doubleTapHandlers = useDoubleTap(() => handleToggleFavoriteScenario(scenario?.id, scenario?.is_favorite));
    const longPressHandlers = useLongPress(handleLongPress);

    return (
      <SwipeableCard
        onDelete={() => handleDeleteScenario(scenario?.id)}
        onArchive={() => handleArchiveScenario(scenario?.id)}
        onEdit={() => {
          // Edit functionality - show info toast
          toast('Edit scenario functionality coming soon', {
            icon: 'ℹ️',
            duration: 3000,
            position: 'top-right'
          });
        }}
        disabled={selectionMode}
      >
        <div 
          {...(selectionMode ? {} : { ...doubleTapHandlers, ...longPressHandlers })}
        >
          {selectionMode && (
            <div className="p-4 pb-0">
              <Checkbox
                checked={selectedScenarios?.has(scenario?.id)}
                onChange={() => handleToggleSelection(scenario?.id)}
                label=""
              />
            </div>
          )}
          <div className="relative">
            {scenario?.is_favorite && (
              <div className="absolute top-4 right-4 z-10">
                <Icon name="Star" size={16} color="var(--color-warning)" fill="var(--color-warning)" />
              </div>
            )}
            <ScenarioCard scenario={scenario} />
          </div>
        </div>
      </SwipeableCard>
    );
  };

  const calculateRetirement = async (formData) => {
    try {
      setCalculating(true);

      // Calculate basic projections
      const yearsToRetirement = formData?.retirementAge - formData?.currentAge;
      const yearsInRetirement = formData?.lifeExpectancy - formData?.retirementAge;
      const targetMonthlyIncome = (formData?.currentExpenses / 12) * (formData?.targetIncomeReplacementRatio / 100);

      // Calculate future values with compound interest
      const calculateFutureValue = (pv, pmt, rate, years) => {
        const monthlyRate = rate / 100 / 12;
        const months = years * 12;
        return pv * Math.pow(1 + monthlyRate, months) + 
               pmt * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
      };

      const totalRRSP = calculateFutureValue(
        formData?.rrspBalance,
        formData?.monthlyRrspContribution,
        formData?.expectedReturnRate,
        yearsToRetirement
      );

      const totalTFSA = calculateFutureValue(
        formData?.tfsaBalance,
        formData?.monthlyTfsaContribution,
        formData?.expectedReturnRate,
        yearsToRetirement
      );

      const totalSavings = totalRRSP + totalTFSA + formData?.otherSavings;

      // Calculate monthly income from savings
      const monthlyIncomeFromSavings = totalSavings / (yearsInRetirement * 12);
      const totalMonthlyIncome = monthlyIncomeFromSavings + 
                                  (formData?.cppEstimatedMonthly || 0) + 
                                  (formData?.oasEstimatedMonthly || 0) + 
                                  (formData?.employerPensionMonthly || 0);

      const incomeGap = targetMonthlyIncome - totalMonthlyIncome;
      const readinessScore = Math.min(100, Math.max(0, (totalMonthlyIncome / targetMonthlyIncome) * 100));

      // Get AI-powered forecast
      const aiPrompt = `As a Canadian retirement planning expert, analyze this retirement scenario and provide a detailed 30-year financial forecast:

Current Situation:
- Age: ${formData?.currentAge}, Retirement Age: ${formData?.retirementAge}
- Annual Income: $${formData?.currentAnnualIncome?.toLocaleString()}
- Current Expenses: $${formData?.currentExpenses?.toLocaleString()}
- RRSP Balance: $${formData?.rrspBalance?.toLocaleString()}
- TFSA Balance: $${formData?.tfsaBalance?.toLocaleString()}
- Monthly RRSP Contribution: $${formData?.monthlyRrspContribution}
- Monthly TFSA Contribution: $${formData?.monthlyTfsaContribution}
- Expected CPP: $${formData?.cppEstimatedMonthly}/month
- Expected OAS: $${formData?.oasEstimatedMonthly}/month

Projected at Retirement:
- Total Savings: $${totalSavings?.toFixed(0)}
- Target Monthly Income: $${targetMonthlyIncome?.toFixed(2)}
- Projected Monthly Income: $${totalMonthlyIncome?.toFixed(2)}
- Income Gap: $${incomeGap?.toFixed(2)}

Provide:
1. Conservative, moderate, and optimistic 30-year projections
2. Retirement readiness assessment
3. Recommended monthly savings to close any gap
4. Tax optimization strategies for RRSP vs TFSA
5. Withdrawal strategy recommendations
6. Key risks and mitigation strategies

Format as JSON with keys: conservative, moderate, optimistic, recommendations, risks`;

      await sendMessage([
        { role: 'system', content: 'You are a Canadian retirement planning expert. Provide detailed, actionable advice.' },
        { role: 'user', content: aiPrompt }
      ], { max_completion_tokens: 2000 });

      // Save retirement plan
      let planId = retirementPlan?.id;
      if (retirementPlan) {
        const { error: updateError } = await supabase?.from('retirement_plans')?.update({
            plan_name: formData?.planName,
            current_age: formData?.currentAge,
            retirement_age: formData?.retirementAge,
            current_annual_income: formData?.currentAnnualIncome,
            current_expenses: formData?.currentExpenses,
            target_income_replacement_ratio: formData?.targetIncomeReplacementRatio,
            rrsp_balance: formData?.rrspBalance,
            tfsa_balance: formData?.tfsaBalance,
            other_savings: formData?.otherSavings,
            monthly_rrsp_contribution: formData?.monthlyRrspContribution,
            monthly_tfsa_contribution: formData?.monthlyTfsaContribution,
            cpp_estimated_monthly: formData?.cppEstimatedMonthly,
            oas_estimated_monthly: formData?.oasEstimatedMonthly,
            employer_pension_monthly: formData?.employerPensionMonthly,
            inflation_rate: formData?.inflationRate,
            expected_return_rate: formData?.expectedReturnRate,
            life_expectancy: formData?.lifeExpectancy,
            readiness_score: readinessScore,
            recommended_monthly_savings: Math.max(0, incomeGap / yearsToRetirement / 12),
            last_calculated_at: new Date()?.toISOString()
          })?.eq('id', retirementPlan?.id)?.eq('user_id', user?.id);

        if (updateError) throw updateError;
      } else {
        const { data: newPlan, error: insertError } = await supabase?.from('retirement_plans')?.insert({
            user_id: user?.id,
            plan_name: formData?.planName,
            current_age: formData?.currentAge,
            retirement_age: formData?.retirementAge,
            current_annual_income: formData?.currentAnnualIncome,
            current_expenses: formData?.currentExpenses,
            target_income_replacement_ratio: formData?.targetIncomeReplacementRatio,
            rrsp_balance: formData?.rrspBalance,
            tfsa_balance: formData?.tfsaBalance,
            other_savings: formData?.otherSavings,
            monthly_rrsp_contribution: formData?.monthlyRrspContribution,
            monthly_tfsa_contribution: formData?.monthlyTfsaContribution,
            cpp_estimated_monthly: formData?.cppEstimatedMonthly,
            oas_estimated_monthly: formData?.oasEstimatedMonthly,
            employer_pension_monthly: formData?.employerPensionMonthly,
            inflation_rate: formData?.inflationRate,
            expected_return_rate: formData?.expectedReturnRate,
            life_expectancy: formData?.lifeExpectancy,
            readiness_score: readinessScore,
            recommended_monthly_savings: Math.max(0, incomeGap / yearsToRetirement / 12),
            last_calculated_at: new Date()?.toISOString()
          })?.select()?.single();

        if (insertError) throw insertError;
        planId = newPlan?.id;
      }

      // Save scenarios
      const scenarioData = [
        {
          retirement_plan_id: planId,
          scenario_type: 'conservative',
          projected_total_savings: totalSavings * 0.85,
          projected_monthly_income: totalMonthlyIncome * 0.85,
          income_gap: incomeGap * 1.15,
          years_until_depletion: Math.floor(yearsInRetirement * 0.8),
          success_probability: Math.max(0, readinessScore - 20)
        },
        {
          retirement_plan_id: planId,
          scenario_type: 'moderate',
          projected_total_savings: totalSavings,
          projected_monthly_income: totalMonthlyIncome,
          income_gap: incomeGap,
          years_until_depletion: yearsInRetirement,
          success_probability: readinessScore
        },
        {
          retirement_plan_id: planId,
          scenario_type: 'optimistic',
          projected_total_savings: totalSavings * 1.15,
          projected_monthly_income: totalMonthlyIncome * 1.15,
          income_gap: incomeGap * 0.85,
          years_until_depletion: Math.floor(yearsInRetirement * 1.2),
          success_probability: Math.min(100, readinessScore + 15)
        }
      ];

      // Delete old scenarios
      await supabase?.from('retirement_scenarios')?.delete()?.eq('retirement_plan_id', planId);

      // Insert new scenarios
      const { error: scenarioError } = await supabase?.from('retirement_scenarios')?.insert(scenarioData);

      if (scenarioError) throw scenarioError;

      toast?.success('Retirement plan calculated successfully');
      setShowForm(false);
      fetchRetirementPlan();
    } catch (error) {
      console.error('Error calculating retirement:', error);
      toast?.error('Failed to calculate retirement plan');
    } finally {
      setCalculating(false);
    }
  };

  const handleFormSubmit = (formData) => {
    calculateRetirement(formData);
  };

  const handleShareRetirementMilestone = () => {
    if (celebrationData) {
      const shareText = `I've reached ${celebrationData?.score}% retirement readiness! 🎯`;
      if (navigator.share) {
        navigator.share({
          title: 'Retirement Milestone!',
          text: shareText
        })?.catch(err => console.log('Share failed:', err));
      } else {
        navigator.clipboard?.writeText(shareText);
        alert('Retirement milestone copied to clipboard!');
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading retirement plan...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (showForm || !retirementPlan) {
    return (
      <Layout>
        <div className="py-6 md:py-8">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
              Retirement Calculator
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Plan your retirement with AI-powered projections
            </p>
          </div>
          <RetirementForm
            plan={retirementPlan}
            onSubmit={handleFormSubmit}
            onClose={() => {
              if (retirementPlan) {
                setShowForm(false);
              }
            }}
            calculating={calculating || aiLoading}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Retirement Calculator</h1>
            <p className="text-gray-600 mt-1">Plan your retirement with detailed scenarios and projections</p>
          </div>

          {/* Error Messages */}
          {errors?.length > 0 && (
            <div className="space-y-3">
              {errors?.map((error) => (
                <ErrorMessage
                  key={error?.id}
                  error={error}
                  onRetry={retryError}
                  onDismiss={removeError}
                />
              ))}
            </div>
          )}

          <div className="py-6 md:py-8 space-y-6 md:space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                  Retirement Calculator
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  {retirementPlan?.plan_name || 'Your Retirement Plan'}
                </p>
              </div>
              {!selectionMode ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    iconName="Edit"
                    onClick={() => setShowForm(true)}
                    className="touch-manipulation min-h-[44px]"
                  >
                    Edit Plan
                  </Button>
                  <Button
                    iconName="RefreshCw"
                    onClick={() => fetchRetirementPlan()}
                    className="touch-manipulation min-h-[44px]"
                  >
                    Recalculate
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={handleCancelSelection}
                  className="touch-manipulation min-h-[44px]"
                >
                  Cancel
                </Button>
              )}
            </div>

            {/* Selection Mode Actions */}
            {selectionMode && (
              <div className="bg-card rounded-xl border p-4 shadow-elevation-1">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-foreground">
                    {selectedScenarios?.size} selected
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAll}
                      className="text-xs"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeselectAll}
                      className="text-xs"
                    >
                      Deselect All
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    iconName="Archive"
                    onClick={handleBulkArchive}
                    disabled={selectedScenarios?.size === 0}
                    className="flex-1 touch-manipulation min-h-[44px]"
                  >
                    Archive
                  </Button>
                  <Button
                    variant="destructive"
                    iconName="Trash2"
                    onClick={handleBulkDelete}
                    disabled={selectedScenarios?.size === 0}
                    className="flex-1 touch-manipulation min-h-[44px]"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )}

            {/* Readiness Score */}
            {retirementPlan?.readiness_score && (
              <ReadinessScore 
                score={retirementPlan?.readiness_score}
                recommendedSavings={retirementPlan?.recommended_monthly_savings || 0}
                yearsToRetirement={(retirementPlan?.retirement_age || 0) - (retirementPlan?.current_age || 0)}
              />
            )}

            {/* Retirement Timeline */}
            <RetirementTimeline 
              plan={retirementPlan}
              currentAge={retirementPlan?.current_age || 0}
              retirementAge={retirementPlan?.retirement_age || 0}
              lifeExpectancy={retirementPlan?.life_expectancy || 0}
              scenarios={scenarios}
            />

            {/* Scenarios */}
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-4">Retirement Scenarios</h2>
              {scenarios?.length === 0 ? (
                <div className="bg-card rounded-xl border p-8 text-center">
                  <Icon name="TrendingUp" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <p className="text-muted-foreground">No scenarios available. Recalculate your plan to generate scenarios.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {scenarios?.map(scenario => (
                    <EnhancedScenarioCard key={scenario?.id} scenario={scenario} />
                  ))}
                </div>
              )}
            </div>

            {/* Government Benefits */}
            <div className="bg-card rounded-xl p-6 border shadow-sm">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Building" size={24} color="var(--color-primary)" />
                Government Benefits (CPP & OAS)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">CPP (Canada Pension Plan)</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${retirementPlan?.cpp_estimated_monthly?.toFixed(2)}/mo
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Based on contribution history and retirement age
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">OAS (Old Age Security)</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${retirementPlan?.oas_estimated_monthly?.toFixed(2)}/mo
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Eligibility at age 65, subject to clawback
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Employer Pension</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${retirementPlan?.employer_pension_monthly?.toFixed(2)}/mo
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Company pension plan contribution
                  </p>
                </div>
              </div>
            </div>

            {/* Contribution Summary */}
            <div className="bg-card rounded-xl p-6 border shadow-sm">
              <h2 className="text-xl font-semibold text-foreground mb-4">Current Contributions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">RRSP Balance</span>
                    <span className="text-lg font-bold text-foreground">
                      ${retirementPlan?.rrsp_balance?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Monthly Contribution</span>
                    <span className="text-lg font-bold text-success">
                      ${retirementPlan?.monthly_rrsp_contribution?.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Tax-deductible contributions with tax-deferred growth
                  </p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">TFSA Balance</span>
                    <span className="text-lg font-bold text-foreground">
                      ${retirementPlan?.tfsa_balance?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Monthly Contribution</span>
                    <span className="text-lg font-bold text-success">
                      ${retirementPlan?.monthly_tfsa_contribution?.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Tax-free withdrawals and growth
                  </p>
                </div>
              </div>
            </div>

            {/* AI Insights */}
            {response && (
              <div className="bg-card rounded-xl p-6 border shadow-sm">
                <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Sparkles" size={24} color="var(--color-primary)" />
                  AI-Powered Insights
                </h2>
                <div className="prose prose-sm max-w-none text-foreground">
                  <pre className="whitespace-pre-wrap text-sm">{response}</pre>
                </div>
              </div>
            )}
          </div>

          <CelebrationModal
            isOpen={showCelebration}
            onClose={() => {
              setShowCelebration(false);
              setCelebrationData(null);
            }}
            title="Retirement Milestone Reached! 🎯"
            message={`Congratulations! You've achieved ${celebrationData?.score}% retirement readiness!`}
            achievementType="savings"
            achievementDetails={celebrationData ? {
              'readiness score': `${celebrationData?.score}%`,
              milestone: `${celebrationData?.threshold}% threshold crossed`
            } : null}
            nextGoalSuggestion={celebrationData?.threshold < 100 ? `Keep contributing to your retirement accounts to reach ${celebrationData?.threshold === 50 ? '75' : celebrationData?.threshold === 75 ? '90' : '100'}% readiness.` : 'You are fully prepared for retirement! Consider reviewing your plan annually.'}
            onShare={handleShareRetirementMilestone}
          />

          {/* Delete Scenario Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteScenarioConfirm}
            onClose={() => {
              setShowDeleteScenarioConfirm(false);
              setScenarioToDelete(null);
            }}
            onConfirm={confirmDeleteScenario}
            title="Delete Retirement Scenario"
            message={`Are you sure you want to delete the "${scenarioToDelete?.scenario_type || 'this'} scenario"?`}
            warningMessage="This will permanently remove this retirement scenario. This action cannot be undone."
            itemDetails={scenarioToDelete ? {
              type: scenarioToDelete?.scenario_type || 'N/A',
              'projected savings': scenarioToDelete?.projected_savings ? `$${parseFloat(scenarioToDelete?.projected_savings)?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}` : 'N/A',
              'readiness score': scenarioToDelete?.readiness_score ? `${scenarioToDelete?.readiness_score}%` : 'N/A'
            } : null}
            confirmLabel="Delete Scenario"
            cancelLabel="Cancel"
            isLoading={deleteScenarioLoading}
          />

          {/* Bulk Delete Scenarios Confirmation Modal */}
          <ConfirmationModal
            isOpen={showBulkDeleteConfirm}
            onClose={() => setShowBulkDeleteConfirm(false)}
            onConfirm={confirmBulkDelete}
            title="Delete Multiple Scenarios"
            message={`Are you sure you want to delete ${selectedScenarios?.size} scenario${selectedScenarios?.size > 1 ? 's' : ''}?`}
            warningMessage="This will permanently remove all selected retirement scenarios. This action cannot be undone."
            confirmLabel={`Delete ${selectedScenarios?.size} Scenario${selectedScenarios?.size > 1 ? 's' : ''}`}
            cancelLabel="Cancel"
            isLoading={bulkDeleteLoading}
          />
        </div>
      </div>
    </Layout>
  );
};

export default RetirementCalculator;